import { ArrowLeftRight, Boxes, Plus, Undo2, Upload } from 'lucide-react';
import { Link } from 'react-router-dom';

import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/cargofl-ui';
import { STOCK_TAKE_WINDOW_DAYS } from '@cfl/assets-shared';

import { PageContainer } from '@/components/shell/page-container';
import { StatTile } from '@/components/stat-tile';
import { assetStatusVariant, warrantyVariant } from '@/lib/badges';
import { useAssets, useAssetSummary } from '@/lib/assets-api';
import { useEmployeeSummary } from '@/lib/employees-api';
import { useExitSummary, useRepairSummary } from '@/lib/operations-api';
import { useCan } from '@/lib/session';
import { useSpendSummary } from '@/lib/spend-api';
import { formatMoney, SpendChart } from '@/components/spend-chart';
import { PILL_CLASS } from '@/lib/badges';

/**
 * The dashboard.
 *
 * Every tile carries a caption saying what the number means in context, never a bare
 * figure - see StatTile. The tiles are ordered by what someone actually comes here to
 * find out: can I issue a laptop today, what is out, and what has gone wrong.
 */
export function DashboardPage() {
  const { data: assets } = useAssetSummary();
  const { data: people } = useEmployeeSummary();
  const { data: repairs } = useRepairSummary();
  const canRunExits = useCan('runExitClearance');
  const { data: exits } = useExitSummary({ enabled: canRunExits });
  const canManage = useCan('manageAssets');

  // The two lists worth surfacing without asking: what is about to go out of warranty,
  // and what has been issued but never confirmed received.
  // Both of these carry an explicit sort. Without one the API defaults to code
  // ascending, so "Recently registered" listed the OLDEST five assets and the warranty
  // card was in sticker order rather than by how soon cover runs out - each heading
  // promising something the list underneath did not do.
  const { data: expiring } = useAssets({
    warranty: 'EXPIRING_SOON',
    pageSize: 5,
    sort: 'warrantyEnd',
    direction: 'asc',
  });
  const { data: recent } = useAssets({ pageSize: 5, sort: 'createdAt', direction: 'desc' });

  const total = assets?.total ?? 0;
  const assigned = assets?.assigned ?? 0;
  const share = total > 0 ? Math.round((assigned / total) * 100) : 0;

  return (
    <PageContainer>
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatTile
          label="Available"
          value={assets?.available ?? 0}
          caption={
            assets?.available
              ? 'ready to issue today'
              : total > 0
                ? 'nothing free to issue'
                : 'no assets registered yet'
          }
          tone={assets && assets.available === 0 && total > 0 ? 'warning' : 'good'}
        />
        <StatTile
          label="Assigned"
          value={assigned}
          caption={total > 0 ? `${share}% of ${total} assets are out with people` : 'no assets yet'}
        />
        <StatTile
          label="Under repair"
          value={assets?.underRepair ?? 0}
          caption={
            repairs?.open
              ? `${repairs.open} repair${repairs.open === 1 ? '' : 's'} still open`
              : 'nothing with a vendor'
          }
          tone={assets?.underRepair ? 'warning' : 'default'}
        />
        <StatTile
          label="Damaged or lost"
          value={(assets?.damaged ?? 0) + (assets?.lost ?? 0)}
          caption={
            (assets?.damaged ?? 0) + (assets?.lost ?? 0) > 0
              ? 'each needs a decision: repair, or write off'
              : 'nothing written off'
          }
          tone={(assets?.damaged ?? 0) + (assets?.lost ?? 0) > 0 ? 'critical' : 'good'}
        />
      </div>

      {/* Five across, where the row above is four: this row gained "Needs checking" and
          five tiles in a four-column grid leaves one orphaned on a line of its own,
          which reads as a tile somebody forgot to finish. */}
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-5">
        <StatTile
          label="Not acknowledged"
          value={assets?.unacknowledged ?? 0}
          caption={
            assets?.unacknowledged
              ? 'issued, but the holder has never confirmed receipt'
              : 'every issued asset has been confirmed'
          }
          tone={assets?.unacknowledged ? 'warning' : 'good'}
        />
        <StatTile
          label="Warranty expiring"
          value={assets?.warrantyExpiringSoon ?? 0}
          caption="cover ends within 90 days"
          tone={assets?.warrantyExpiringSoon ? 'warning' : 'default'}
        />
        {/*
          The one question the register could never answer: has anyone actually seen
          this stuff. Amber rather than red even when everything is overdue - on the day
          this shipped every asset was unverified, and a red tile for a job nobody has
          started yet reads as a fault rather than as work.
        */}
        <StatTile
          label="Needs checking"
          value={assets?.verificationDue ?? 0}
          caption={
            assets?.verificationDue
              ? `not physically seen in ${STOCK_TAKE_WINDOW_DAYS} days`
              : 'everything has been seen recently'
          }
          tone={assets?.verificationDue ? 'warning' : 'good'}
        />
        <StatTile
          label="People holding assets"
          value={people?.holdingAssets ?? 0}
          caption={`of ${people?.active ?? 0} active employee${people?.active === 1 ? '' : 's'}`}
        />
        {canRunExits ? (
          <StatTile
            label="Left, still holding"
            value={exits?.exitedHoldingAssets ?? 0}
            caption={
              exits?.exitedHoldingAssets
                ? 'exited employees who never returned everything'
                : 'everyone who left returned everything'
            }
            tone={exits?.exitedHoldingAssets ? 'critical' : 'good'}
          />
        ) : (
          <StatTile
            label="Archived"
            value={assets?.archived ?? 0}
            caption="retired or disposed of, kept for the record"
          />
        )}
      </div>

      {canManage && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm">Quick actions</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-wrap gap-2">
            <Button hint={"Add a new asset to the register"} asChild size="sm">
              <Link to="/assets/new">
                <Plus className="mr-1.5 size-4" /> Register asset
              </Link>
            </Button>
            <Button hint={"Hand an asset to an employee"} asChild size="sm" variant="secondary">
              <Link to="/assign">
                <ArrowLeftRight className="mr-1.5 size-4" /> Assign
              </Link>
            </Button>
            <Button hint={"Record that an asset came back"} asChild size="sm" variant="secondary">
              <Link to="/return">
                <Undo2 className="mr-1.5 size-4" /> Record a return
              </Link>
            </Button>
            <Button hint={"Add many assets or employees at once from a spreadsheet"} asChild size="sm" variant="secondary">
              <Link to="/import">
                <Upload className="mr-1.5 size-4" /> Bulk import
              </Link>
            </Button>
          </CardContent>
        </Card>
      )}

      <SpendSection />

      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm">Warranty running out</CardTitle>
            <CardDescription className="text-xs">
              Cover ending within 90 days. Assets with no warranty recorded are left out.
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-0">
            {expiring?.assets.length ? (
              <ul className="divide-y">
                {expiring.assets.map((asset) => (
                  <li key={asset.id} className="flex items-center gap-3 py-2.5">
                    <Link
                      to={`/assets/${asset.id}`}
                      className="min-w-0 flex-1 hover:underline"
                    >
                      <div className="truncate text-sm font-medium">{asset.name}</div>
                      <div className="at-num truncate text-xs text-muted-foreground">
                        {asset.code}
                        {asset.holder ? ` · ${asset.holder.name}` : ''}
                      </div>
                    </Link>
                    <Badge variant={warrantyVariant(asset.warranty)} className={PILL_CLASS}>
                      {asset.warrantyEnd}
                    </Badge>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="py-6 text-center text-sm text-muted-foreground">
                Nothing goes out of warranty in the next 90 days.
              </p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm">Recently registered</CardTitle>
            <CardDescription className="text-xs">
              The most recent additions to the register.
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-0">
            {recent?.assets.length ? (
              <ul className="divide-y">
                {recent.assets.map((asset) => (
                  <li key={asset.id} className="flex items-center gap-3 py-2.5">
                    <Link to={`/assets/${asset.id}`} className="min-w-0 flex-1 hover:underline">
                      <div className="truncate text-sm font-medium">{asset.name}</div>
                      <div className="at-num truncate text-xs text-muted-foreground">
                        {asset.code} · {asset.categoryName}
                      </div>
                    </Link>
                    <Badge variant={assetStatusVariant(asset.status)} className={PILL_CLASS}>
                      {asset.status.replace(/_/g, ' ').toLowerCase()}
                    </Badge>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="space-y-3 py-8 text-center">
                <Boxes className="mx-auto size-8 text-muted-foreground/50" />
                <p className="text-sm text-muted-foreground">
                  No assets registered yet.
                  {canManage ? ' Add one, or import a spreadsheet.' : ''}
                </p>
                {canManage && (
                  <Button hint={"Add many assets or employees at once from a spreadsheet"} asChild size="sm" variant="secondary">
                    <Link to="/import">Bulk import</Link>
                  </Button>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </PageContainer>
  );
}

/**
 * What has been spent, when, and on how many repairs.
 *
 * Gated on `viewReports` rather than `viewRegister`: what the company has paid is a
 * finance question, and IT needing to see the register does not mean they need the
 * invoice totals. The API enforces the same gate.
 *
 * The two totals are kept apart rather than added into one headline. Purchase cost is
 * capital spent once; repair cost is the number that creeps up quietly and that
 * nobody budgets for. A single "total spend" figure would bury the second inside the
 * first, which is exactly the number worth watching.
 */
function SpendSection() {
  const canSeeReports = useCan('viewReports');
  const { data: spend, isPending } = useSpendSummary({ enabled: canSeeReports });

  if (!canSeeReports) return null;

  const assetTotal = Number(spend?.assets.total ?? 0);
  const inServiceTotal = Number(spend?.assets.inService ?? 0);
  const repairTotal = Number(spend?.repairs.total ?? 0);
  // The share repairs make up of everything spent. The interesting number: 3% is
  // normal running cost, 30% means the fleet is old and should be replaced.
  const repairShare =
    assetTotal + repairTotal > 0
      ? Math.round((repairTotal / (assetTotal + repairTotal)) * 100)
      : 0;

  return (
    <div className="space-y-4">
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatTile
          label="Spent on assets"
          value={formatMoney(assetTotal)}
          caption={
            spend?.assets.missingPrice
              ? `across ${spend.assets.priced} assets · ${spend.assets.missingPrice} have no price recorded`
              : `across ${spend?.assets.priced ?? 0} assets with a price recorded`
          }
          // Amber when the figure is materially incomplete: a total that covers half
          // the register is not the company's asset value, and presenting it as
          // though it were is how a dashboard becomes untrustworthy.
          tone={spend && spend.assets.missingPrice > spend.assets.priced ? 'warning' : 'default'}
        />
        <StatTile
          label="Spent on repairs"
          value={formatMoney(repairTotal)}
          caption={
            repairTotal > 0
              ? `${repairShare}% of everything spent so far`
              : 'nothing spent on repairs yet'
          }
          tone={repairShare >= 25 ? 'warning' : 'default'}
        />
        {/*
          Two figures, not one, and deliberately side by side.

          "Spent on assets" is all time and includes anything since scrapped - the
          money was spent, and a total that shrinks when you dispose of a laptop can
          never be reconciled against finance's ledger. "Still in service" answers the
          different question of what the company currently owns. Publishing only the
          first overstates what you have; only the second understates what you spent.
        */}
        <StatTile
          label="Still in service"
          value={formatMoney(inServiceTotal)}
          caption={
            spend?.assets.archived
              ? `${formatMoney(assetTotal - inServiceTotal)} of it retired or disposed of`
              : 'nothing retired or disposed of yet'
          }
        />
        <StatTile
          label="Repairs logged"
          value={spend?.repairs.count ?? 0}
          caption={
            spend && spend.repairs.count > 0
              ? `${formatMoney(repairTotal / spend.repairs.count)} average${spend.repairs.open ? ` · ${spend.repairs.open} still open` : ''}`
              : 'no repairs recorded yet'
          }
          tone={spend?.repairs.open ? 'warning' : 'default'}
        />
      </div>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm">Spend by month</CardTitle>
          <CardDescription className="text-xs">
            The last 12 months. Purchases are dated from the purchase date, repairs from
            the day the asset went to the vendor — that is when the money is committed.
          </CardDescription>
        </CardHeader>
        <CardContent className="pt-0">
          {isPending ? (
            <p className="py-10 text-center text-sm text-muted-foreground">Loading spend…</p>
          ) : (
            <SpendChart months={spend?.months ?? []} />
          )}
        </CardContent>
      </Card>

      {spend && spend.topCategories.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm">Where the money went</CardTitle>
            <CardDescription className="text-xs">
              Purchase cost by asset type, biggest first.
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-0">
            <ul className="space-y-2.5">
              {spend.topCategories.map((category) => {
                const share = assetTotal > 0 ? (Number(category.total) / assetTotal) * 100 : 0;
                return (
                  <li key={category.name} className="space-y-1">
                    <div className="flex items-baseline justify-between gap-3 text-sm">
                      <span className="truncate">
                        {category.name}
                        <span className="ml-1.5 text-xs text-muted-foreground">
                          ×{category.count}
                        </span>
                      </span>
                      <span className="at-num shrink-0 font-medium">
                        {formatMoney(category.total)}
                      </span>
                    </div>
                    {/* One measure, one hue - a magnitude bar, not a categorical
                        palette. Sharing --chart-1 with the purchases series is
                        deliberate: both mean "money spent buying things". */}
                    <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
                      <div
                        className="h-full rounded-full"
                        style={{
                          width: `${Math.max(share, 1.5)}%`,
                          background: 'hsl(var(--chart-1))',
                        }}
                      />
                    </div>
                  </li>
                );
              })}
            </ul>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
